import { useFormik } from 'formik';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { BsFillPatchQuestionFill } from 'react-icons/bs';


import Modal from '../../../../components/modal';
import Input from '../../../../components/input';
import SearchableSelect from '../../../../components/searchableSelect';
import EmptyState from '../../../../components/emptyState';
import { Drawer } from '../../../../components/Drawer';
import { showSuccessMessage, setFormikErrors } from '../../../../hooks/constants';
import close from '../../../../assets/svg-icons/closeBrand.svg';
import arrow from '../../../../assets/svg-icons/blueArrow.svg';
import editIcon from '../../../../assets/svg-icons/edit.svg';
import deleteIcon from '../../../../assets/svg-icons/deleteRed.svg';

export default function IpsecPoliciesTab({
  filtered,
  selectedIds,
  toggleAll,
  toggleOne,
  onOpenCreate,
  onRefresh,
  onRequestDelete,
  projectId,
  apiEndPoints,
  setLoading,
  refetchAll,
  enumOptions,
  isPolicyInUse,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [manageModalIndex, setManageModalIndex] = useState(null);

  const editForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name || '',
      description: editing?.description || '',
      auth_algorithm: editing?.auth_algorithm || 'sha1',
      encryption_algorithm: editing?.encryption_algorithm || 'aes-128',
      encapsulation_mode: editing?.encapsulation_mode || 'tunnel',
      transform_protocol: editing?.transform_protocol || 'esp',
      pfs: editing?.pfs || 'group5',
      units: editing?.units || 'seconds',
      value: editing?.value || 3600,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      auth_algorithm: Yup.string().required('Authorization algorithm is required'),
      encryption_algorithm: Yup.string().required('Encryption algorithm is required'),
      encapsulation_mode: Yup.string().required('Encapsulation mode is required'),
      transform_protocol: Yup.string().required('Transform protocol is required'),
      pfs: Yup.string().required('PFS is required'),
      units: Yup.string().required('Lifetime units is required'),
      value: Yup.number().required('Lifetime value is required').min(60, 'Min 60 seconds'),
    }),
    onSubmit: async (values) => {
      if (!projectId || !editing) return;
      setLoading(true);
      try {
          const payload = { ...values };

          if (!payload.description?.trim()) {
            delete payload.description;
          }

          await apiEndPoints.updateIpsecPolicy(projectId, editing.id || editing.uuid, payload);
        showSuccessMessage('IPSec Policy updated');
        setEditOpen(false);
        setEditing(null);
        await refetchAll();
      } catch (error) {
        setFormikErrors(error, editForm.setErrors);
      } finally {
        setLoading(false);
      }
    },
  });

  const rows = useMemo(() => (Array.isArray(filtered) ? filtered : []), [filtered]);
  
  const rowIds = rows
  .map((row) => row.id || row.uuid)
  .filter(Boolean);

const allRowsSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));


  const closeManageModal = () => setManageModalIndex(null);

  return (
    <>
      <table className="w-full text-sm text-left overflow-hidden">
        <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border dark:border-[#c4c4c432] text-sm font-semibold">
          <tr>
               <th className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={allRowsSelected}
            onChange={toggleAll}
          />
        </th>
            <th className="px-6 py-4 whitespace-nowrap">Name</th>
            <th className="px-6 py-4 whitespace-nowrap">Description</th>
            <th className="px-6 py-4 whitespace-nowrap">Authorization algorithm</th>
            <th className="px-6 py-4 whitespace-nowrap">Encryption algorithm</th>
            <th className="px-6 py-4 whitespace-nowrap">PFS</th>
            <th className="px-6 py-4 whitespace-nowrap">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-slate-800 text-black/50 dark:text-white/60 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] text-sm">
          {rows.length === 0 ? (
            <tr>
              <td className="px-6 py-10" colSpan={7}>
                <EmptyState
                  title="No IPSec policies yet"
                  description="Create an IPSec Policy to define Phase 2 negotiation parameters."
                  primaryAction={{ label: 'Add IPSec Policy', onClick: onOpenCreate }}
                  secondaryAction={{ label: 'Refresh', onClick: onRefresh }}
                />
              </td>
            </tr>
          ) : (
            rows.map((row, index) => {
              const id = row.id || row.uuid;
              const policyInUse = isPolicyInUse?.(id);
              return (
                <tr
                  key={id}
                  className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                >
                  <td className="text-start py-4 px-6">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(id)}
                      onChange={() => toggleOne(id)}
                    />
                  </td>
                  <td className="text-start py-4 px-6">
                    <button
                      type="button"
                      className="text-primaryBlue hover:underline"
                      onClick={() => {
                        setSelected(row);
                        setDetailsOpen(true);
                      }}
                    >
                      {row.name || '—'}
                    </button>
                  </td>
                  <td className="text-start py-4 px-6">{row.description || '—'}</td>
                  <td className="text-start py-4 px-6">{row.auth_algorithm || '—'}</td>
                  <td className="text-start py-4 px-6">{row.encryption_algorithm || '—'}</td>
                  <td className="text-start py-4 px-6">{row.pfs || '—'}</td>
                  <td className="text-start py-4 px-6 relative">
                    <button
                      className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                      onClick={() => setManageModalIndex(index)}
                      type="button"
                    >
                      Manage IPSec Policy
                    </button>

                    {manageModalIndex === index && (
                      <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                        <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                          <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                            <div className="flex gap-2 items-center">
                              <span className="dark:text-white text-black font-medium">
                                Manage "{row.name}"
                              </span>
                            </div>
                            <img
                              src={close}
                              onClick={closeManageModal}
                              className="h-5 cursor-pointer"
                              alt=""
                            />
                          </div>
                          <div className="p-4 grid gap-1">
                           <div
                              className={`flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 transition-all text-black font-medium ${
                                policyInUse
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:bg-primaryBlue/5 cursor-pointer'
                              }`}
                              onClick={() => {
                                if (policyInUse) return;

                                setEditing(row);
                                setEditOpen(true);
                                closeManageModal();
                              }}
                              title={policyInUse ? 'This IPSec Policy is already used by an IPSec Site Connection' : ''}
                            >
                              <div className="flex items-center gap-2">
                                <img src={editIcon} alt="" className="h-4" />
                                <span>Edit IPSec Policy</span>
                              </div>
                              <img src={arrow} alt="" />
                            </div>

                            <div
                          className={`flex items-center justify-between hover:rounded border-b dark:border-[#c4c4c424] last:border-none py-4 px-3 transition-all font-medium ${
                            policyInUse
                              ? 'opacity-50 cursor-not-allowed text-red-300'
                              : 'hover:bg-primaryBlue/5 text-red-500 cursor-pointer'
                          }`}
                          onClick={() => {
                            if (policyInUse) return;

                            onRequestDelete(id, row.name || id, 'Delete IPSec Policy');
                            closeManageModal();
                          }}
                          title={policyInUse ? 'This IPSec Policy is already used by an IPSec Site Connection' : ''}
                        >
                          <div className="flex items-center gap-2">
                            <img src={deleteIcon} alt="" className="h-4" />
                            <span>Delete IPSec Policy</span>
                          </div>
                          <img src={arrow} alt="" />
                        </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <Modal
        title="Edit IPSec Policy"
        isVisible={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        size="md:w-[60%]"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <form className="grid gap-4" onSubmit={editForm.handleSubmit}>
            <Input
              label="Name *"
              name="name"
              value={editForm.values.name}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.name ? editForm.errors.name : null}
              isLabelIcon={true}
              iconTitle="The name of the IPsec policy."
            />
            <Input
              label="Description"
              name="description"
              value={editForm.values.description}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.description ? editForm.errors.description : null}
              isLabelIcon={true}
              iconTitle="A description for the IPsec policy."
            />
            <SearchableSelect
              label="Authorization algorithm *"
              name="auth_algorithm"
              value={editForm.values.auth_algorithm}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions?.auth_algorithm || []}
              onError={editForm.touched.auth_algorithm ? editForm.errors.auth_algorithm : null}
              isLabelIcon={true}
              iconTitle="Valid algorithms are sha1, sha256, sha384 and sha512."
            />
            <SearchableSelect
              label="Encryption algorithm *"
              name="encryption_algorithm"
              value={editForm.values.encryption_algorithm}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions?.encryption_algorithm || []}
              onError={editForm.touched.encryption_algorithm ? editForm.errors.encryption_algorithm : null}
              isLabelIcon={true}
              iconTitle="Valid algorithms are 3des, aes-128, aes-192 and aes-256."
            />
            <SearchableSelect
              label="Encapsulation mode *"
              name="encapsulation_mode"
              value={editForm.values.encapsulation_mode}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions?.encapsulation_mode || []}
              onError={editForm.touched.encapsulation_mode ? editForm.errors.encapsulation_mode : null}
              isLabelIcon={true}
              iconTitle="The type of IPsec tunnel (tunnel/transport) to be used."
            />
            <SearchableSelect
              label="Transform Protocol *"
              name="transform_protocol"
              value={editForm.values.transform_protocol}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions?.transform_protocol || []}
              onError={editForm.touched.transform_protocol ? editForm.errors.transform_protocol : null}
              isLabelIcon={true}
              iconTitle="The type of protocol (esp, ah, ah-esp) used in IPsec policy."
            />
            <SearchableSelect
              label="Perfect Forward Secrecy *"
              name="pfs"
              value={editForm.values.pfs}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions?.pfs || []}
              onError={editForm.touched.pfs ? editForm.errors.pfs : null}
              isLabelIcon={true}
              iconTitle="PFS limited to using Diffie-Hellman groups 2, 5 (default) and 14."
            />
            <SearchableSelect
              label="Lifetime Units *"
              name="units"
              value={editForm.values.units}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              options={enumOptions?.units || []}
              onError={editForm.touched.units ? editForm.errors.units : null}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />
            <Input
              label="Lifetime Value *"
              name="value"
              type="number"
              value={editForm.values.value}
              onChange={editForm.handleChange}
              onBlur={editForm.handleBlur}
              onError={editForm.touched.value ? editForm.errors.value : null}
              isLabelIcon={true}
              iconTitle="Life time consists of units and value. Units in 'seconds' and the default value is 3600."
            />

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-xs border-[1.5px] border-[#c4c4c460] rounded-md cursor-pointer bg-white dark:bg-slate-800"
                onClick={() => {
                  setEditOpen(false);
                  setEditing(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-xs rounded-md cursor-pointer bg-primaryBlue text-white hover:opacity-90"
              >
                Save Changes
              </button>
            </div>
          </form>

          <div className="hidden md:block">
            <p className="text-2xl font-semibold">Description:</p>
            <p className="text-sm opacity-80 mt-1">You may update IPSec policy details here.</p>
          </div>
        </div>
      </Modal>

      <Drawer
        open={detailsOpen}
        title={selected?.name || 'IPSec Policy'}
        onClose={() => {
          setDetailsOpen(false);
          setSelected(null);
        }}
        width={620}
      >
        {selected ? (
          <div className="grid gap-6">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xl font-semibold">{selected.name}</div>
              {!isPolicyInUse?.(selected.id || selected.uuid) && (
                <button
                  type="button"
                  className="text-xs rounded-md px-3 py-2 cursor-pointer bg-white dark:bg-slate-800 border border-[#c4c4c460] hover:bg-brandBlue/10"
                  onClick={() => {
                    setEditing(selected);
                    setEditOpen(true);
                  }}
                >
                  Edit IPSec Policy
                </button>
              )}
            </div>

            <div className="border dark:border-[#c4c4c432] rounded-md overflow-hidden">
              {[
                ['Name', selected.name],
                ['Description', selected.description],
                ['ID', selected.id || selected.uuid],
                ['Project ID', selected.project_id],
                ['Authorization algorithm', selected.auth_algorithm],
                ['Encapsulation mode', selected.encapsulation_mode],
                ['Encryption algorithm', selected.encryption_algorithm],
                ['Transform Protocol', selected.transform_protocol],
                ['Lifetime Units', selected.units ?? selected.lifetime?.units ?? 'seconds'],
                ['Lifetime Value', selected.value ?? selected.lifetime?.value ?? 3600],
                ['Perfect Forward Secrecy', selected.pfs],
              ].map(([k, v]) => {
                const fieldDescriptions = {
                  Name: 'The name of the IPsec policy.',
                  Description: 'A description for the IPsec policy.',
                  'Authorization algorithm': 'Valid algorithms are sha1, sha256, sha384 and sha512.',
                  'Encapsulation mode': 'The type of IPsec tunnel (tunnel/transport) to be used.',
                  'Encryption algorithm': 'Valid algorithms are 3des, aes-128, aes-192 and aes-256.',
                  'Lifetime Units': 'Life time consists of units and value. Units in \'seconds\' and the default value is 3600.',
                  'Lifetime Value': 'Life time consists of units and value. Units in \'seconds\' and the default value is 3600.',
                  'Perfect Forward Secrecy': 'PFS limited to using Diffie-Hellman groups 2, 5 (default) and 14.',
                  'Transform Protocol': 'The type of protocol (esp, ah, ah-esp) used in IPsec policy.',
                };

                return (
                  <div
                    key={k}
                    className="grid grid-cols-3 gap-3 px-4 py-3 border-b last:border-b-0 dark:border-[#c4c4c432]"
                  >
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center">
                      {k}
                      {fieldDescriptions[k] && (
                        <BsFillPatchQuestionFill
                          className="text-xs text-primaryBlue ml-1 cursor-pointer"
                          title={fieldDescriptions[k]}
                        />
                      )}
                    </div>
                    <div className="col-span-2 text-sm text-gray-900 dark:text-white">
                      {v ?? '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-sm opacity-70">No policy selected.</div>
        )}
      </Drawer>
    </>
  );
}

